import { doGET, doPUT } from "../../util/HttpUtil";
import { tokenUtil } from "../../util/TokenUtil";
import {
  buildSpecialEmailEntry,
  buildSpecialEmailsPayload,
  doSpecialEmailsMatch,
} from "./inviteMemberUtils";

const hasSpecialEmailsField = (applicationForm) => (
  Boolean(applicationForm && Object.prototype.hasOwnProperty.call(applicationForm, "specialEmails"))
);

const normalizeEddRequestLookup = (lookup) => (
  typeof lookup === "string"
    ? { eddRequestId: lookup }
    : (lookup || {})
);

const buildEddRequestDetailEndpoint = (lookup) => {
  const { eddRequestId, applicationId } = normalizeEddRequestLookup(lookup);

  if (applicationId) {
    return `/api/edd-request/detail?application_id=${encodeURIComponent(applicationId)}`;
  }

  if (eddRequestId) {
    return `/api/edd-request/detail?id=${encodeURIComponent(eddRequestId)}`;
  }

  throw new Error("Periodic review application id is required.");
};

const buildEddRequestUpdatePayload = (latestEddRequest, specialEmails) => {
  const payload = {
    _id: latestEddRequest?._id,
    specialEmails,
  };

  if (latestEddRequest?.status !== undefined && latestEddRequest?.status !== null) {
    payload.status = latestEddRequest.status;
  }

  if (latestEddRequest?.application_status !== undefined && latestEddRequest?.application_status !== null) {
    payload.application_status = latestEddRequest.application_status;
  }

  return payload;
};

export const fetchLatestApplicationForm = async (formId) => {
  if (!formId) {
    throw new Error("Application form id is required.");
  }

  const response = await doGET(`/api/application-form/detail?id=${encodeURIComponent(formId)}&withExtension=true`);

  if (response?.status !== 200 || !response?.data) {
    throw new Error("Unable to fetch latest application form.");
  }

  return response.data;
};

export const fetchLatestEddRequest = async (lookup, { suppressErrors = false } = {}) => {
  const endpoint = buildEddRequestDetailEndpoint(lookup);
  const response = suppressErrors
    ? await tokenUtil.callAPI({
      endPoint: endpoint,
      method: "GET",
      reqParam: {},
      token: await tokenUtil.getBearerToken(),
    })
    : await doGET(endpoint);

  if (suppressErrors && response?.status !== 200) {
    return null;
  }

  if (response?.status !== 200 || !response?.data) {
    throw new Error("Unable to fetch latest periodic review invite access.");
  }

  return response.data;
};

export const updateSpecialEmails = async ({ formId, specialEmails }) => {
  const latestApplicationForm = await fetchLatestApplicationForm(formId);
  const payload = buildSpecialEmailsPayload(latestApplicationForm, specialEmails);
  const response = await doPUT("/api/application-form/update", payload);

  if (response?.status !== 200) {
    throw new Error("Unable to update invited member access.");
  }

  let updatedApplicationForm = response?.data || {};

  if (!hasSpecialEmailsField(updatedApplicationForm)) {
    updatedApplicationForm = await fetchLatestApplicationForm(formId);
  }

  if (!doSpecialEmailsMatch(updatedApplicationForm?.specialEmails, payload.specialEmails)) {
    throw new Error("Backend did not persist invited member access. Verify /api/application-form/update supports specialEmails.");
  }

  return {
    ...updatedApplicationForm,
    specialEmails: payload.specialEmails,
  };
};

export const saveSpecialEmail = async ({ formId, name, email }) => {
  const specialEmail = buildSpecialEmailEntry({ name, email });

  if (!specialEmail) {
    throw new Error("Invited member email is required.");
  }

  return updateSpecialEmails({ formId, specialEmails: [specialEmail] });
};

export const removeSpecialEmail = async ({ formId }) => {
  return updateSpecialEmails({ formId, specialEmails: [] });
};

export const updateEddSpecialEmails = async ({ eddRequestId, applicationId, specialEmails }) => {
  const payloadSpecialEmails = Array.isArray(specialEmails)
    ? specialEmails.map(buildSpecialEmailEntry).filter(Boolean)
    : [];
  const latestEddRequest = await fetchLatestEddRequest({ eddRequestId, applicationId });
  const resolvedEddRequestId = latestEddRequest?._id || eddRequestId;

  if (!resolvedEddRequestId) {
    throw new Error("Periodic review request id is required.");
  }

  const payload = buildEddRequestUpdatePayload(latestEddRequest, payloadSpecialEmails);
  const response = await doPUT(`/api/edd-request/update?id=${encodeURIComponent(resolvedEddRequestId)}`, payload);

  if (response?.status !== 200) {
    throw new Error("Unable to update periodic review invite access.");
  }

  const updatedEddRequest = response?.data || {};

  if (!doSpecialEmailsMatch(updatedEddRequest?.specialEmails, payloadSpecialEmails)) {
    throw new Error("Backend did not persist periodic review invited member access. Verify /api/edd-request/update supports specialEmails.");
  }

  return {
    ...updatedEddRequest,
    specialEmails: payloadSpecialEmails,
  };
};

export const saveEddSpecialEmail = async ({ eddRequestId, applicationId, name, email }) => {
  const specialEmail = buildSpecialEmailEntry({ name, email });

  if (!specialEmail) {
    throw new Error("Invited member email is required.");
  }

  return updateEddSpecialEmails({ eddRequestId, applicationId, specialEmails: [specialEmail] });
};

export const removeEddSpecialEmail = async ({ eddRequestId, applicationId }) => {
  return updateEddSpecialEmails({ eddRequestId, applicationId, specialEmails: [] });
};
